import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type {
  CallsignScheduleCallsignsItem,
  CallsignSchedulePeriod,
} from '@transreport/pauk-rrms-api'
import { getEffectiveCallsignSchedulePeriods } from '@transreport/pauk-rrms-api'
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns'
import { CalendarDays, ChevronLeft, ChevronRight, PanelRightOpen } from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppTooltip, SkeletonBlock } from '@/components/framework'
import { useOperatingStations } from '@/hooks/useOperatingStations'
import { trackEvent } from '@/lib/analytics'
import { isOvernightTimeRange } from '@/lib/timeline/time'

import { ShiftsAgendaDrawer } from './ShiftsAgendaDrawer'
import { getCallsignColourClass, getEffectivePeriodForDate } from './shiftScheduleVisualisation'

export { getCallsignColourClass } from './shiftScheduleVisualisation'

type DragRange = {
  start: string
  end: string
}

type WeekEventSegment = {
  callsigns: CallsignScheduleCallsignsItem[]
  period: CallsignSchedulePeriod
  startDate: Date
  startIndex: number
  laneKey: string
  lane: number
  isCarryIn: boolean
}

type WeekEventLayout = {
  segments: WeekEventSegment[]
  laneCount: number
}

const CALENDAR_SKELETON_CELLS = Array.from({ length: 42 }, (_, index) => index)
const MAX_VISIBLE_CALLSIGN_ROWS = 3

const ShiftsCalendarSkeleton = ({ label }: { label: string }) => (
  <div
    role="status"
    aria-label={label}
    className="absolute inset-0 z-30 flex flex-col bg-white p-2 sm:p-3"
  >
    <SkeletonBlock className="mb-2 h-5 w-36" />
    <div className="grid grid-cols-7 gap-2 border-y border-gray-200 bg-gray-50 p-2">
      {CALENDAR_SKELETON_CELLS.slice(0, 7).map((cell) => (
        <SkeletonBlock key={cell} className="mx-auto h-3 w-10" />
      ))}
    </div>
    <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6 border-l border-gray-200">
      {CALENDAR_SKELETON_CELLS.map((cell) => (
        <div key={cell} className="border-r border-b border-gray-200 p-2">
          <SkeletonBlock className="size-4" />
          <SkeletonBlock className="mt-3 h-3 w-full" />
          <SkeletonBlock className="mt-2 h-3 w-3/4" />
        </div>
      ))}
    </div>
  </div>
)

function hasSplitApplicabilityForDate(periods: CallsignSchedulePeriod[], date: Date): boolean {
  const dateKey = format(date, 'yyyy-MM-dd')
  return periods.some((period) => {
    const applicability = period.applicability ?? period.schedule.applicability
    return (
      period.active_from <= dateKey &&
      dateKey < period.active_until &&
      (applicability === 'weekdays' || applicability === 'weekends')
    )
  })
}

function getCalendarDays(month: Date): Date[] {
  return eachDayOfInterval({
    start: startOfWeek(startOfMonth(month), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(month), { weekStartsOn: 1 }),
  })
}

function getOrderedRange(range: DragRange): DragRange {
  return range.start <= range.end ? range : { start: range.end, end: range.start }
}

function getShiftLaneKey(callsign: CallsignScheduleCallsignsItem): string {
  const isOvernight =
    callsign.ends_next_day || isOvernightTimeRange(callsign.start_time, callsign.end_time)
  return `${callsign.start_time}-${callsign.end_time}-${isOvernight ? 'overnight' : 'same-day'}`
}

export function getWeekEventLayout(
  days: Date[],
  periods: CallsignSchedulePeriod[]
): WeekEventLayout {
  const segments: WeekEventSegment[] = []
  const segmentByKey = new Map<string, WeekEventSegment>()
  const lanePositions = new Map<string, number>()
  const firstDay = days[0]
  const previousDay = firstDay ? addDays(firstDay, -1) : null
  const previousPeriod = previousDay ? getEffectivePeriodForDate(periods, previousDay) : null

  const addSegment = (
    callsign: CallsignScheduleCallsignsItem,
    period: CallsignSchedulePeriod,
    startDate: Date,
    startIndex: number,
    isCarryIn: boolean
  ) => {
    const laneKey = getShiftLaneKey(callsign)
    const segmentKey = `${startIndex}-${laneKey}`
    const existingSegment = segmentByKey.get(segmentKey)
    lanePositions.set(
      laneKey,
      Math.min(lanePositions.get(laneKey) ?? Number.POSITIVE_INFINITY, callsign.position)
    )

    if (existingSegment) {
      existingSegment.callsigns.push(callsign)
      return
    }

    const segment = {
      callsigns: [callsign],
      period,
      startDate,
      startIndex,
      laneKey,
      lane: 0,
      isCarryIn,
    }
    segmentByKey.set(segmentKey, segment)
    segments.push(segment)
  }

  if (previousDay && previousPeriod) {
    for (const callsign of previousPeriod.schedule.callsigns) {
      if (!callsign.ends_next_day && !isOvernightTimeRange(callsign.start_time, callsign.end_time))
        continue
      addSegment(callsign, previousPeriod, previousDay, -1, true)
    }
  }

  days.forEach((day, dayIndex) => {
    const period = getEffectivePeriodForDate(periods, day)
    if (!period) return
    for (const callsign of period.schedule.callsigns) {
      addSegment(callsign, period, day, dayIndex, false)
    }
  })

  const lanes = [...lanePositions].toSorted(
    ([leftKey, leftPosition], [rightKey, rightPosition]) =>
      leftPosition - rightPosition || leftKey.localeCompare(rightKey)
  )
  const laneByKey = new Map(lanes.map(([laneKey], index) => [laneKey, index]))
  for (const segment of segments) {
    segment.lane = laneByKey.get(segment.laneKey) ?? 0
  }

  return { segments, laneCount: lanes.length }
}

export const ShiftsCalendarPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const today = useMemo(() => startOfDay(new Date()), [])
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(today))
  const [jumpDate, setJumpDate] = useState(() => format(today, 'yyyy-MM-dd'))
  const [agendaDate, setAgendaDate] = useState(today)
  const [isAgendaOpen, setIsAgendaOpen] = useState(false)
  const [dragRange, setDragRange] = useState<DragRange | null>(null)
  const [overrideRange, setOverrideRange] = useState<DragRange>(() => ({
    start: format(today, 'yyyy-MM-dd'),
    end: format(today, 'yyyy-MM-dd'),
  }))
  const dragRangeRef = useRef<DragRange | null>(null)
  const { selectedStationCode } = useOperatingStations()

  const rangeStart = format(startOfMonth(visibleMonth), 'yyyy-MM-dd')
  const rangeEnd = format(addMonths(startOfMonth(visibleMonth), 1), 'yyyy-MM-dd')
  const todayKey = format(today, 'yyyy-MM-dd')

  const periodsQuery = useQuery({
    queryKey: ['callsignSchedule', 'calendar', rangeStart, rangeEnd, selectedStationCode],
    queryFn: () =>
      getEffectiveCallsignSchedulePeriods({
        active_from: rangeStart,
        active_until: rangeEnd,
        station_code: selectedStationCode ?? '',
      }),
    enabled: Boolean(selectedStationCode),
  })

  const periods = periodsQuery.data ?? []
  const hasSplitSchedule = periods.some((period) => {
    const applicability = period.applicability ?? period.schedule.applicability
    return applicability === 'weekdays' || applicability === 'weekends'
  })

  const showMonth = (targetDate: Date) => {
    const targetMonth = startOfMonth(targetDate)
    setVisibleMonth(targetMonth)
    setJumpDate(format(targetDate, 'yyyy-MM-dd'))
  }

  const openOverrideEditor = (range: DragRange) => {
    const orderedRange = getOrderedRange(range)
    void navigate({
      to: '/shifts/override',
      search: { from: orderedRange.start, to: orderedRange.end },
    })
  }

  const openAgenda = (date: Date) => {
    setAgendaDate(startOfDay(date))
    setJumpDate(format(date, 'yyyy-MM-dd'))
    setIsAgendaOpen(true)
    trackEvent('agenda_opened', { area: 'schedule' })
  }

  const focusCalendarDate = (date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd')
    setJumpDate(dateKey)
    if (!isSameMonth(date, visibleMonth)) setVisibleMonth(startOfMonth(date))
    window.requestAnimationFrame(() => {
      document.getElementById(`calendar-day-${dateKey}`)?.focus()
    })
  }

  const changeAgendaDate = (date: Date) => {
    const normalisedDate = startOfDay(date)
    setAgendaDate(normalisedDate)
    setJumpDate(format(normalisedDate, 'yyyy-MM-dd'))
    if (!isSameMonth(normalisedDate, visibleMonth)) setVisibleMonth(startOfMonth(normalisedDate))
  }

  useEffect(() => {
    const finishDrag = () => {
      const range = dragRangeRef.current
      dragRangeRef.current = null
      setDragRange(null)
      if (!range) return

      const orderedRange = getOrderedRange(range)
      if (orderedRange.start === orderedRange.end || orderedRange.start < todayKey) {
        openAgenda(parseISO(orderedRange.start))
        return
      }
      openOverrideEditor(orderedRange)
    }

    window.addEventListener('pointerup', finishDrag)
    window.addEventListener('pointercancel', finishDrag)
    return () => {
      window.removeEventListener('pointerup', finishDrag)
      window.removeEventListener('pointercancel', finishDrag)
    }
  })

  const openScheduleForDate = (period: CallsignSchedulePeriod, date: Date) => {
    const dateKey = format(date, 'yyyy-MM-dd')
    if (period.schedule.schedule_type === 'override') {
      const inclusiveEnd = format(
        addDays(parseISO(period.schedule.active_until ?? period.active_until), -1),
        'yyyy-MM-dd'
      )
      void navigate({
        to: '/shifts/override',
        search: {
          from: period.schedule.active_from < dateKey ? dateKey : period.schedule.active_from,
          to: inclusiveEnd < dateKey ? dateKey : inclusiveEnd,
        },
      })
      return
    }

    void navigate({ to: '/shifts/default', search: { on: dateKey } })
  }

  const orderedDragRange = dragRange ? getOrderedRange(dragRange) : null
  const calendarDays = getCalendarDays(visibleMonth)
  const weekCount = calendarDays.length / 7
  const monthKey = format(visibleMonth, 'yyyy-MM')
  const weekLayouts = Array.from({ length: weekCount }, (_, index) => {
    const days = calendarDays.slice(index * 7, index * 7 + 7)
    return { days, ...getWeekEventLayout(days, periods) }
  })

  return (
    <div className="flex size-full min-h-0 flex-col bg-gray-100">
      <header
        data-shifts-calendar-header
        className="z-10 flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 bg-white p-3 shadow-sm sm:px-6"
      >
        <h1 className="sr-only">{t('resources.calendar.title')}</h1>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          {hasSplitSchedule && (
            <div className="mr-2 hidden items-center gap-3 text-xs text-gray-700 lg:flex">
              <span className="inline-flex items-center gap-1.5">
                <span className="size-3 rounded-sm border border-emerald-300 bg-emerald-50" />
                {t('resources.calendar.weekdays')}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="size-3 rounded-sm border border-amber-200 bg-amber-50" />
                {t('resources.calendar.weekends')}
              </span>
            </div>
          )}
          <button
            type="button"
            aria-pressed={isAgendaOpen}
            onClick={() => {
              if (isAgendaOpen) {
                setIsAgendaOpen(false)
                return
              }
              openAgenda(jumpDate ? parseISO(jumpDate) : today)
            }}
            className={`inline-flex h-9 cursor-pointer items-center gap-2 rounded border px-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${
              isAgendaOpen
                ? 'border-primary bg-primary text-white'
                : 'border-border-gray bg-white text-gray-800 hover:bg-gray-50'
            }`}
          >
            <PanelRightOpen className="size-4" aria-hidden="true" />
            {t('resources.calendar.dayAgenda')}
          </button>
          <button
            type="button"
            onClick={() => showMonth(addMonths(visibleMonth, -1))}
            className="inline-flex size-11 cursor-pointer items-center justify-center rounded border border-border-gray bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            aria-label={t('resources.calendar.previousMonth')}
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => showMonth(today)}
            className="inline-flex h-11 cursor-pointer items-center gap-2 rounded border border-border-gray bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50"
          >
            <CalendarDays className="size-4" aria-hidden="true" />
            {t('resources.calendar.today')}
          </button>
          <button
            type="button"
            onClick={() => showMonth(addMonths(visibleMonth, 1))}
            className="inline-flex size-11 cursor-pointer items-center justify-center rounded border border-border-gray bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            aria-label={t('resources.calendar.nextMonth')}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </button>
          <label htmlFor="shifts-calendar-jump-date" className="sr-only">
            {t('resources.calendar.jumpToDate')}
          </label>
          <input
            id="shifts-calendar-jump-date"
            type="date"
            value={jumpDate}
            onChange={(event) => {
              const value = event.target.value
              setJumpDate(value)
              if (!value) return

              const date = parseISO(value)
              if (Number.isNaN(date.getTime())) return
              showMonth(date)
            }}
            className="h-11 rounded border border-border-gray px-2 text-sm"
          />
        </div>

        <div className="flex w-full flex-wrap items-end gap-2 border-t border-gray-200 pt-3">
          <div>
            <label
              htmlFor="shifts-override-range-start"
              className="mb-1 block text-xs font-medium text-text-gray-700"
            >
              {t('resources.calendar.overrideFrom')}
            </label>
            <input
              id="shifts-override-range-start"
              type="date"
              min={todayKey}
              value={overrideRange.start}
              onChange={(event) =>
                setOverrideRange((current) => ({ ...current, start: event.target.value }))
              }
              className="h-11 rounded border border-border-gray px-2 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="shifts-override-range-end"
              className="mb-1 block text-xs font-medium text-text-gray-700"
            >
              {t('resources.calendar.overrideTo')}
            </label>
            <input
              id="shifts-override-range-end"
              type="date"
              min={overrideRange.start || todayKey}
              value={overrideRange.end}
              onChange={(event) =>
                setOverrideRange((current) => ({ ...current, end: event.target.value }))
              }
              className="h-11 rounded border border-border-gray px-2 text-sm"
            />
          </div>
          <button
            type="button"
            disabled={
              !overrideRange.start ||
              !overrideRange.end ||
              overrideRange.start < todayKey ||
              overrideRange.end < overrideRange.start
            }
            onClick={() => openOverrideEditor(overrideRange)}
            className="inline-flex min-h-11 items-center rounded border border-primary bg-primary px-3 text-sm font-semibold text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('resources.calendar.createOverride')}
          </button>
          <p className="text-xs text-text-gray-500">{t('resources.calendar.rangeHint')}</p>
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div data-calendar-background className="h-full min-h-0">
          {!selectedStationCode && (
            <div className="m-6 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-700 shadow-sm">
              {t('common.selectStationFirst')}
            </div>
          )}

          {selectedStationCode && periodsQuery.error && (
            <div
              role="alert"
              className="m-6 rounded-lg border border-red-300 bg-red-50 p-6 text-sm text-red-800"
            >
              {t('resources.calendar.loadError')}
            </div>
          )}

          {selectedStationCode && !periodsQuery.error && (
            <div
              className="relative h-full min-h-0 overflow-hidden bg-white"
              aria-busy={periodsQuery.isLoading}
            >
              {periodsQuery.isLoading && (
                <ShiftsCalendarSkeleton label={t('resources.calendar.loading')} />
              )}
              <section
                aria-hidden={periodsQuery.isLoading}
                className={`flex h-full min-h-0 flex-col bg-white p-2 sm:p-3 ${periodsQuery.isLoading ? 'invisible' : ''}`}
                aria-labelledby={`month-${monthKey}`}
              >
                <div className="flex items-center justify-between pb-2">
                  <h2 id={`month-${monthKey}`} className="text-base font-semibold text-gray-900">
                    {format(visibleMonth, 'MMMM yyyy')}
                  </h2>
                </div>
                <div className="grid grid-cols-7 border-y border-gray-200 bg-gray-50 text-center text-xs font-semibold tracking-wide text-gray-500 uppercase">
                  {calendarDays.slice(0, 7).map((day) => (
                    <div key={format(day, 'EEE')} className="px-1 py-1.5">
                      {format(day, 'EEE')}
                    </div>
                  ))}
                </div>
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto border-l border-gray-200">
                  {weekLayouts.map(({ days, segments, laneCount }) => {
                    const weekKey = format(days[0], 'yyyy-MM-dd')
                    const visibleLaneCount = Math.min(laneCount, MAX_VISIBLE_CALLSIGN_ROWS)
                    const visibleSegments = segments.filter(
                      (segment) => segment.lane < MAX_VISIBLE_CALLSIGN_ROWS
                    )
                    const overflowCounts = days.map((_, dayIndex) =>
                      segments.reduce(
                        (count, segment) =>
                          segment.startIndex === dayIndex &&
                          segment.lane >= MAX_VISIBLE_CALLSIGN_ROWS
                            ? count + segment.callsigns.length
                            : count,
                        0
                      )
                    )

                    return (
                      <div key={weekKey} className="relative grid min-h-30 flex-1 grid-cols-7">
                        {days.map((day) => {
                          const dateKey = format(day, 'yyyy-MM-dd')
                          const isInMonth = isSameMonth(day, visibleMonth)
                          const isPast = isBefore(day, today)
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6
                          const hasSplitScheduleOnDate = hasSplitApplicabilityForDate(periods, day)
                          const isSelected =
                            orderedDragRange &&
                            orderedDragRange.start <= dateKey &&
                            dateKey <= orderedDragRange.end
                          const backgroundClass = !isInMonth
                            ? 'bg-gray-50 text-gray-600'
                            : isSelected
                              ? 'bg-brand-surface text-gray-900'
                              : hasSplitScheduleOnDate
                                ? isWeekend
                                  ? 'bg-amber-50 text-gray-900'
                                  : 'bg-emerald-50 text-gray-900'
                                : 'bg-white text-gray-900'

                          return (
                            <button
                              type="button"
                              key={dateKey}
                              id={`calendar-day-${dateKey}`}
                              disabled={!isInMonth}
                              tabIndex={isInMonth && dateKey === jumpDate ? 0 : -1}
                              onPointerDown={(event) => {
                                if (!isInMonth || event.button !== 0) return
                                event.preventDefault()
                                const range = { start: dateKey, end: dateKey }
                                dragRangeRef.current = range
                                setDragRange(range)
                              }}
                              onPointerEnter={() => {
                                if (!isInMonth || !dragRangeRef.current) return
                                if (dragRangeRef.current.start < todayKey || dateKey < todayKey)
                                  return
                                const range = { ...dragRangeRef.current, end: dateKey }
                                dragRangeRef.current = range
                                setDragRange(range)
                              }}
                              onKeyDown={(event) => {
                                if (!isInMonth) return
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  openAgenda(day)
                                  return
                                }

                                let targetDate: Date | null = null
                                if (event.key === 'ArrowRight') targetDate = addDays(day, 1)
                                if (event.key === 'ArrowLeft') targetDate = addDays(day, -1)
                                if (event.key === 'ArrowDown') targetDate = addDays(day, 7)
                                if (event.key === 'ArrowUp') targetDate = addDays(day, -7)
                                if (event.key === 'Home')
                                  targetDate = startOfWeek(day, { weekStartsOn: 1 })
                                if (event.key === 'End')
                                  targetDate = addDays(startOfWeek(day, { weekStartsOn: 1 }), 6)
                                if (event.key === 'PageUp') targetDate = addMonths(day, -1)
                                if (event.key === 'PageDown') targetDate = addMonths(day, 1)
                                if (!targetDate) return
                                event.preventDefault()
                                focusCalendarDate(targetDate)
                              }}
                              className={`min-w-0 border-r border-b border-gray-200 p-1 text-left transition-colors disabled:cursor-default ${backgroundClass} ${
                                isSameDay(day, today)
                                  ? 'relative z-1 ring-2 ring-primary ring-inset'
                                  : ''
                              } ${
                                !isSameDay(day, today) && dateKey === jumpDate
                                  ? 'relative z-1 ring-2 ring-primary ring-inset'
                                  : ''
                              } ${
                                isInMonth
                                  ? isPast
                                    ? 'cursor-pointer select-none'
                                    : 'cursor-crosshair select-none'
                                  : ''
                              }`}
                              aria-label={format(day, 'EEEE d MMMM yyyy')}
                              aria-current={isSameDay(day, today) ? 'date' : undefined}
                              aria-haspopup={isInMonth ? 'dialog' : undefined}
                            />
                          )
                        })}

                        <div
                          data-calendar-date-layer
                          className="pointer-events-none absolute inset-x-0 top-0 z-20 grid h-7 grid-cols-7"
                          aria-hidden="true"
                        >
                          {days.map((day) => {
                            const dateKey = format(day, 'yyyy-MM-dd')
                            const isInMonth = isSameMonth(day, visibleMonth)
                            const isToday = isSameDay(day, today)
                            const period = getEffectivePeriodForDate(periods, day)

                            return (
                              <div
                                key={dateKey}
                                className="flex min-w-0 items-start justify-between gap-0.5 px-1 pt-1"
                              >
                                <span
                                  data-calendar-date={dateKey}
                                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-semibold ${
                                    isToday
                                      ? 'bg-primary text-white shadow-sm'
                                      : isInMonth
                                        ? 'text-gray-900'
                                        : 'text-gray-600'
                                  }`}
                                >
                                  {format(day, 'd')}
                                </span>
                                {period?.schedule.schedule_type === 'override' && isInMonth && (
                                  <span className="truncate rounded bg-purple-100 px-1 py-0.5 text-xs leading-none font-semibold text-purple-800 uppercase">
                                    {t('resources.calendar.override')}
                                  </span>
                                )}
                              </div>
                            )
                          })}
                        </div>

                        {visibleLaneCount > 0 && (
                          <div
                            className="pointer-events-none absolute inset-x-0 top-7 bottom-5 z-10 grid grid-cols-7 gap-y-px overflow-hidden"
                            style={{
                              gridTemplateRows: `repeat(${visibleLaneCount}, minmax(1.5rem, 1fr))`,
                            }}
                          >
                            {visibleSegments.map((segment) => {
                              const [callsign] = segment.callsigns
                              if (!callsign) return null

                              const isEntirelyPast = isBefore(segment.startDate, today)
                              const editableDate = isBefore(segment.startDate, today)
                                ? today
                                : segment.startDate
                              const names = segment.callsigns.map((item) => item.name).join(', ')
                              const userNames = segment.callsigns
                                .flatMap((item) => (item.user_name ? [item.user_name] : []))
                                .join(', ')
                              const isOvernight =
                                callsign.ends_next_day ||
                                isOvernightTimeRange(callsign.start_time, callsign.end_time)
                              const overnightStart = segment.isCarryIn
                                ? 0
                                : segment.startIndex + 0.5
                              const overnightWidth = segment.isCarryIn ? 0.5 : 1
                              const timeLabel = `${callsign.start_time}–${callsign.end_time}`

                              return (
                                <button
                                  key={`${segment.laneKey}-${format(segment.startDate, 'yyyy-MM-dd')}-${segment.startIndex}`}
                                  type="button"
                                  data-start-date={format(segment.startDate, 'yyyy-MM-dd')}
                                  data-callsign-count={segment.callsigns.length}
                                  data-overnight={isOvernight || undefined}
                                  data-carry-in={segment.isCarryIn || undefined}
                                  disabled={isEntirelyPast}
                                  onPointerDown={(event) => event.stopPropagation()}
                                  onClick={() => openScheduleForDate(segment.period, editableDate)}
                                  aria-label={`${names}: ${timeLabel}${
                                    isOvernight ? ` · ${t('resources.continuesNextDay')}` : ''
                                  }${userNames ? ` · ${userNames}` : ''}`}
                                  title={`${names}: ${timeLabel}${
                                    isOvernight ? ` · ${t('resources.overnight')}` : ''
                                  }${userNames ? ` · ${userNames}` : ''}`}
                                  style={
                                    isOvernight
                                      ? {
                                          left: `${(overnightStart / 7) * 100}%`,
                                          width: `${(overnightWidth / 7) * 100}%`,
                                          top: `${(segment.lane / visibleLaneCount) * 100}%`,
                                          height: `calc(${100 / visibleLaneCount}% - 1px)`,
                                        }
                                      : {
                                          gridColumn: `${segment.startIndex + 1} / span 1`,
                                          gridRow: segment.lane + 1,
                                        }
                                  }
                                  className={`pointer-events-auto min-h-6 min-w-0 cursor-pointer self-stretch overflow-hidden rounded border px-1 text-left text-[clamp(0.55rem,1.15vh,0.7rem)] leading-none font-semibold shadow-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:cursor-default disabled:opacity-60 ${isOvernight ? 'absolute' : 'mx-0.5'} ${getCallsignColourClass(
                                    callsign.name
                                  )}`}
                                >
                                  <span className="flex h-full min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap">
                                    <span
                                      data-callsign-badges
                                      className="flex min-w-0 gap-1 overflow-hidden"
                                    >
                                      {segment.callsigns.map((item) => (
                                        <AppTooltip
                                          key={item.id}
                                          content={item.user_name || undefined}
                                        >
                                          <span
                                            data-callsign-badge={item.name}
                                            className="shrink-0 rounded-sm border border-current/20 bg-white/70 px-1.5 py-0.5 leading-none"
                                          >
                                            {item.name}
                                          </span>
                                        </AppTooltip>
                                      ))}
                                    </span>
                                    {segment.callsigns.length === 1 && callsign.user_name && (
                                      <span className="truncate font-medium opacity-90">
                                        · {callsign.user_name}
                                      </span>
                                    )}
                                    <span className="ml-auto hidden shrink-0 font-normal opacity-75 sm:inline">
                                      {timeLabel}
                                    </span>
                                    {segment.callsigns.length === 1 && !callsign.user_name && (
                                      <span className="truncate font-normal opacity-75 sm:hidden">
                                        {timeLabel}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              )
                            })}
                          </div>
                        )}

                        {overflowCounts.some((count) => count > 0) && (
                          <div
                            data-calendar-overflow-layer
                            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 grid h-5 grid-cols-7"
                          >
                            {days.map((day, dayIndex) => {
                              const count = overflowCounts[dayIndex] ?? 0
                              if (count === 0) return <div key={format(day, 'yyyy-MM-dd')} />

                              const dateLabel = format(day, 'EEEE d MMMM yyyy')
                              return (
                                <button
                                  key={format(day, 'yyyy-MM-dd')}
                                  type="button"
                                  data-calendar-overflow={format(day, 'yyyy-MM-dd')}
                                  onPointerDown={(event) => event.stopPropagation()}
                                  onClick={() => openAgenda(day)}
                                  aria-label={t('resources.calendar.moreCallsignsAria', {
                                    count,
                                    date: dateLabel,
                                  })}
                                  title={t('resources.calendar.moreCallsignsAria', {
                                    count,
                                    date: dateLabel,
                                  })}
                                  className="pointer-events-auto mx-0.5 min-w-0 cursor-pointer truncate rounded-t bg-gray-100 px-1 text-left text-[0.625rem] leading-5 font-semibold text-brand-ink hover:bg-brand-surface focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                                >
                                  {t('resources.calendar.moreCallsigns', { count })}
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </section>
            </div>
          )}
        </div>
        <ShiftsAgendaDrawer
          isOpen={isAgendaOpen}
          selectedDate={agendaDate}
          onDateChange={changeAgendaDate}
          onClose={() => setIsAgendaOpen(false)}
        />
      </div>
    </div>
  )
}
